"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { usePathname } from "next/navigation";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getApiUrl } from "@/app/_module/services/config";
import { isDevelopment } from "@/app/_module/utils/environment-config";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { removeRealtimeChannel } from "../services/realtimeTeardown";
import { rateGate } from "@/app/_module/utils/rateGate";


/**
 * Routine presence logging (heartbeat sent/skipped/succeeded, offline beacons).
 *
 * These fire every ~30s, so in production they flooded the user-visible console
 * and buried real errors — the Turnstile failures sat unnoticed underneath them.
 * They also echoed the backend URL on every tick, which is public but pointless
 * to keep reprinting. Silenced outside development; console.error/warn are left
 * alone so genuine failures still surface.
 */
const presenceDebug = (...args: unknown[]) => {
  if (isDevelopment()) console.debug(...(args as []));
};

export interface UserPresenceState {
  oderId: string;
  userId: string;
  name: string;
  email: string;
  userType: string;
  currentPage: string;
  lastSeen: string;
  device: string;
  browser: string;
  os: string;
  status: "online" | "idle" | "offline";
}

/**
 * Hook to track and broadcast the current user's presence
 * This should be used in the main layout to broadcast presence to admins
 */
export function usePresenceBroadcast() {
  const { user, refetchUser } = useUserContext();
  const pathname = usePathname();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isIdleRef = useRef(false);

  // Parse user agent
  const getUserAgentInfo = useCallback(() => {
    if (typeof window === "undefined") {
      return { device: "Unknown", browser: "Unknown", os: "Unknown" };
    }

    const ua = navigator.userAgent;

    // Detect browser
    let browser = "Unknown";
    if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Edg/")) browser = "Edge";
    else if (ua.includes("Chrome")) browser = "Chrome";
    else if (ua.includes("Safari")) browser = "Safari";

    // Detect OS
    let os = "Unknown";
    if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac OS")) os = "macOS";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

    // Detect device
    let device = "Desktop";
    if (ua.includes("Mobile") || ua.includes("Android")) device = "Mobile";
    else if (ua.includes("Tablet") || ua.includes("iPad")) device = "Tablet";

    return { device, browser, os };
  }, []);

  // Get page name from pathname
  const getPageName = useCallback((path: string) => {
    if (!path || path === "/") return "Dashboard";

    const segments = path.split("/").filter(Boolean);
    if (segments.length === 0) return "Dashboard";

    // Map common routes to friendly names
    const routeMap: Record<string, string> = {
      dashboard: "Dashboard",
      leads: "Leads",
      communication: "Communication",
      "admin-panel": "Admin Panel",
      settings: "Settings",
      "agency-dashboard": "Agency Dashboard",
      calendar: "Calendar",
      reports: "Reports",
      policies: "Policies",
    };

    const firstSegment = segments[0];
    return routeMap[firstSegment] || firstSegment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  useEffect(() => {
    if (!user?.user?.id) return;

    let channel: RealtimeChannel;
    // Cache access token and API URL for synchronous use in beforeunload handler
    // beforeunload handlers must be synchronous - async functions won't complete before browser closes
    let cachedAccessToken: string | null = null;
    let cachedStatusUrl: string | null = null;

    const setupPresence = async () => {
      try {
        const supabase = await getSupabaseClient();
        const { device, browser, os } = getUserAgentInfo();

        // Cache the access token and status URL for beforeunload handler
        // Use supabaseAuthService which has localStorage fallback for multi-device scenarios
        const currentSession = await supabaseAuthService.getCurrentSession();
        cachedAccessToken = currentSession?.access_token || null;
        // Pre-compute the status URL for synchronous use in beforeunload
        try {
          cachedStatusUrl = getApiUrl('users/status');
        } catch {
          cachedStatusUrl = null;
        }

        // ⚠️ supabase.channel() returns the CACHED instance when a channel
        // with this topic already exists — and attaching .on("presence") to an
        // already-subscribed channel throws "cannot add presence callbacks
        // after subscribe()". Happens on every remount of the layout (login
        // transitions, /m ↔ desktop). Tear the old one down first so this is
        // always a fresh channel.
        const stale = supabase
          .getChannels()
          .find((c: any) => c.topic === "realtime:user-presence");
        if (stale) {
          try { await supabase.removeChannel(stale); } catch { /* already gone */ }
        }

        // Create presence channel - all users join the same channel
        channel = supabase.channel("user-presence", {
          config: {
            presence: {
              key: user.user.id,
            },
          },
        });

        channel
          .on("presence", { event: "sync" }, () => {
            // Sync event received
          })
          .subscribe(async (status) => {
            if (status === "SUBSCRIBED") {
              // Track presence with user info and current page
              const trackData = {
                userId: user.user.id,
                name: user.user.name || "Unknown",
                email: user.user.email || "",
                userType: user.user.userType || "agent",
                currentPage: getPageName(pathname),
                lastSeen: new Date().toISOString(),
                device,
                browser,
                os,
                status: "online" as const,
              };
              await channel.track(trackData);
            }
          });

        channelRef.current = channel;
      } catch (error) {
        console.error("[PresenceBroadcast] Setup error:", error);
      }
    };

    setupPresence();

    // Activity detection for idle status
    const activityEvents = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (isIdleRef.current && channelRef.current) {
        isIdleRef.current = false;
        // Update presence to online
        const { device, browser, os } = getUserAgentInfo();
        channelRef.current.track({
          userId: user.user.id,
          name: user.user.name || "Unknown",
          email: user.user.email || "",
          userType: user.user.userType || "agent",
          currentPage: getPageName(pathname),
          lastSeen: new Date().toISOString(),
          device,
          browser,
          os,
          status: "online" as const,
        });
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Heartbeat function to update lastSeenAt in database
    // IMPORTANT: Only sends heartbeat if user is ACTIVE (not idle)
    // This ensures lastSeenAt becomes stale when user is away, triggering call forwarding
    const sendHeartbeat = async (force = false): Promise<boolean> => {
      try {
        // Skip heartbeat if user is idle (unless forced, e.g., on initial mount)
        // This allows lastSeenAt to become stale after 5 minutes of inactivity
        // which triggers automatic call forwarding to their phone
        if (!force && isIdleRef.current) {
          presenceDebug('[Heartbeat] Skipped - user is idle');
          return false;
        }

        // Also check if user has been inactive for more than 5 minutes
        // This catches cases where idle status hasn't been set yet
        const idleTime = Date.now() - lastActivityRef.current;
        if (!force && idleTime > 5 * 60 * 1000) {
          presenceDebug('[Heartbeat] Skipped - user inactive for', Math.round(idleTime / 60000), 'minutes');
          return false;
        }

        // Use supabaseAuthService which has localStorage fallback for multi-device scenarios
        const currentSession = await supabaseAuthService.getCurrentSession();
        const accessToken = currentSession?.access_token;

        if (accessToken) {
          // Update cached token for beforeunload handler
          cachedAccessToken = accessToken;

          // Update lastSeenAt in database via backend API
          // Use getApiUrl to properly construct URL with /api prefix
          try {
            const heartbeatUrl = getApiUrl('users/heartbeat');
            // Also update cached status URL while we're at it
            if (!cachedStatusUrl) {
              cachedStatusUrl = getApiUrl('users/status');
            }

            presenceDebug('[Heartbeat] Sending heartbeat to:', heartbeatUrl);

            // At most one heartbeat per 10 s per user, however often this effect
            // re-runs (Safari 26 storm, 2026-09-17). A failed one is not cached,
            // so runInitialSync's retries still reach the server.
            await rateGate(`heartbeat:${user.user.id}`, 10_000, async () => {
              const response = await fetch(heartbeatUrl, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
              });
              if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
            });
            presenceDebug('[Heartbeat] Success - lastSeenAt updated');
            return true;
          } catch (fetchError) {
            console.error('[Heartbeat] Failed:', fetchError);
            return false;
          }
        } else {
          console.warn('[Heartbeat] No access token available - skipping');
        }
      } catch (error) {
        console.error('[Heartbeat] Error:', error);
      }
      return false;
    };

    // Send initial heartbeat immediately on mount (forced)
    //
    // ⚠️ AND THEN SYNC THE LOCAL COPY. This is the "logged in but shows offline" bug.
    // The pagehide beacon writes user_status='offline' when the last session ended, so
    // /auth/profile returns 'offline' at the next login and the header dot + status
    // selector both read that value straight out of the user context. The heartbeat
    // immediately corrects the DATABASE (users.controller flips 'offline' -> 'available'),
    // but nothing told the CLIENT, so the UI sat on the stale 'offline' until the user
    // picked a status by hand — which is the only thing that refreshed the context.
    //
    //   beacon -> DB 'offline' -> login -> context 'offline' -> heartbeat fixes DB
    //                                            ^ never updated -- the bug
    //
    // ⚠️ This MIRRORS the server's rule (only promote from offline/absent, never
    // overwrite a deliberate dnd / on_call). If that rule changes in
    // user.controller.ts updateHeartbeat, change it here too.
    // RETRY the initial sync: on the native app this effect mounts before the
    // auth token is rehydrated, so the first forced heartbeat used to skip
    // ("no access token") and nothing ever mirrored — the dot sat on
    // 'offline' until the user picked a status by hand. Back off a few times
    // until the token exists, then run the same mirror rule.
    let initialSyncTimer: ReturnType<typeof setTimeout> | null = null;
    // Mirror the server's offline->available promotion into the client after
    // ANY successful heartbeat (initial or the 2-minute interval). Only after
    // success — flipping the dot green after a FAILED heartbeat would show
    // "available" while the database still says offline, and calls route on
    // the database, not on the dot. ONLY explicit 'offline' is promoted —
    // the mirror must never overwrite a deliberate dnd / on_call.
    const mirrorPromotion = (ok: boolean | undefined) => {
      if (!ok) return;
      refetchUser((prev: any) => {
        const current = prev?.user?.userStatus;
        if (current !== 'offline') return prev;
        return { ...prev, user: { ...(prev?.user ?? {}), userStatus: 'available' } };
      });
    };
    const runInitialSync = (attempt: number) => {
      void sendHeartbeat(true).then((ok) => {
        if (!ok) {
          if (attempt < 5) {
            initialSyncTimer = setTimeout(() => runInitialSync(attempt + 1), 2000 * (attempt + 1));
          }
          return;
        }
        mirrorPromotion(ok);
      });
    };
    runInitialSync(0);

    // Update database lastSeenAt every 2 minutes (heartbeat for offline detection)
    // NOTE: Heartbeat only sends when user is ACTIVE - skips when idle
    // This enables automatic call forwarding when user is away from computer
    const heartbeatInterval = setInterval(() => { void sendHeartbeat(false).then(mirrorPromotion); }, 2 * 60 * 1000); // Every 2 minutes

    // Check for idle every 30 seconds
    const idleCheckInterval = setInterval(() => {
      const idleTime = Date.now() - lastActivityRef.current;
      if (idleTime > 5 * 60 * 1000 && !isIdleRef.current && channelRef.current) {
        // 5 minutes = idle
        isIdleRef.current = true;
        const { device, browser, os } = getUserAgentInfo();
        channelRef.current.track({
          userId: user.user.id,
          name: user.user.name || "Unknown",
          email: user.user.email || "",
          userType: user.user.userType || "agent",
          currentPage: getPageName(pathname),
          lastSeen: new Date().toISOString(),
          device,
          browser,
          os,
          status: "idle" as const,
        });
      }
    }, 30000);

    // Tab close / refresh presence handling
    //
    // Problem: pagehide fires for both refresh and tab close. We can't tell them apart
    // during the teardown sequence because sessionStorage isn't cleared until after
    // both beforeunload and pagehide have already fired.
    //
    // Strategy (send-always, recover-on-refresh):
    // 1. beforeunload: set REFRESH_FLAG_KEY in sessionStorage (persists through refresh, gone on tab close)
    // 2. pagehide: ALWAYS send the offline beacon (for both refresh and tab close)
    // 3. On mount: check for REFRESH_FLAG_KEY — if it exists, this is a refresh,
    //    so send an "available" status after 1.5s delay to override the beacon
    // 4. On tab close: the flag is set but no new page loads, so no recovery happens → user stays offline (correct)

    const REFRESH_FLAG_KEY = 'insuracentral_page_unloading';
    let isIntentionalNavigation = false;

    // Track clicks on links to detect intentional navigation
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('a[href]') || target.closest('button[type="submit"]')) {
        isIntentionalNavigation = true;
      }
    };
    document.addEventListener('click', handleLinkClick, { capture: true });

    // Track form submissions
    const handleFormSubmit = () => {
      isIntentionalNavigation = true;
    };
    document.addEventListener('submit', handleFormSubmit, { capture: true });

    // beforeunload fires for refresh, tab close, and navigation
    const handleBeforeUnload = () => {
      try {
        // Set a flag that will persist through refresh but not through tab close
        sessionStorage.setItem(REFRESH_FLAG_KEY, Date.now().toString());
      } catch {
        // Silent fail
      }
    };

    // visibilitychange to 'hidden' can indicate tab is being closed
    // Combined with pagehide, we can make a better determination
    let visibilityHiddenAt = 0;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        visibilityHiddenAt = Date.now();
      } else {
        visibilityHiddenAt = 0; // Reset when visible again
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // pagehide is the most reliable event for detecting when user leaves
    const handlePageHide = (event: PageTransitionEvent) => {
      // If page is being put in bfcache (back-forward cache), don't set offline
      // User can come back instantly with back button
      if (event.persisted) {
        return;
      }

      if (isIntentionalNavigation) {
        presenceDebug('[Presence] Link/form navigation detected - not sending offline beacon');
        return;
      }

      // Always send the offline beacon for both refresh and tab close.
      // On refresh, the new page will detect the REFRESH_FLAG_KEY in sessionStorage
      // and send a follow-up "available" status to correct it.
      // On tab close, the user genuinely goes offline.
      // Try cached token first, fall back to reading from localStorage directly
      // (handles multi-device scenario where Supabase client has no session)
      let tokenForBeacon = cachedAccessToken;
      if (!tokenForBeacon) {
        try {
          // Read from our session backup (synchronous localStorage read is safe in pagehide)
          const backupRaw = localStorage.getItem('insuracentral_session_backup');
          if (backupRaw) {
            const backup = JSON.parse(backupRaw);
            tokenForBeacon = backup?.access_token || null;
          }
        } catch { /* ignore */ }
      }
      let urlForBeacon = cachedStatusUrl;
      if (!urlForBeacon) {
        try { urlForBeacon = getApiUrl('users/status'); } catch { /* ignore */ }
      }
      if (tokenForBeacon && urlForBeacon) {
        try {
          const payload = JSON.stringify({ status: 'offline' });
          navigator.sendBeacon(
            `${urlForBeacon}?token=${tokenForBeacon}`,
            new Blob([payload], { type: 'application/json' })
          );
          presenceDebug('[Presence] Offline beacon sent');
        } catch (err) {
          // Failed to send offline beacon - best effort
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    // Detect page refresh and re-establish "available" status.
    // On refresh: beforeunload set the REFRESH_FLAG_KEY, pagehide sent an offline beacon,
    // and now the new page has mounted. We send an "available" status after a short delay
    // to guarantee it arrives after the offline beacon has been processed by the server.
    // On fresh login / first load: the flag won't exist, so this is a no-op.
    let refreshRecoveryTimer: ReturnType<typeof setTimeout> | undefined;
    let refreshRecoverySecondTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const refreshFlag = sessionStorage.getItem(REFRESH_FLAG_KEY);
      if (refreshFlag) {
        presenceDebug('[Presence] Page refresh detected — will re-set available status');
        sessionStorage.removeItem(REFRESH_FLAG_KEY);

        // Re-assert "available" TWICE after a refresh, not once.
        //
        // The offline beacon goes out via sendBeacon during pagehide, and we have no
        // control over when the server actually processes it. A single recovery POST
        // at 1.5s is a RACE: if the beacon lands late (slow network, backend under
        // load), it overwrites our "available" and the agent is left showing offline
        // while they're sitting right there — the reported bug. A second pass a few
        // seconds later costs one tiny request and closes that window.
        const reassertAvailable = async (label: string) => {
          try {
            const currentSession = await supabaseAuthService.getCurrentSession();
            const token = currentSession?.access_token;
            const url = cachedStatusUrl || getApiUrl('users/status');
            if (token && url) {
              await fetch(`${url}?token=${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'available' }),
              });
              presenceDebug(`[Presence] Re-established available status after refresh (${label})`);
            }
          } catch {
            // Best-effort — heartbeat will eventually correct it
          }
        };

        refreshRecoveryTimer = setTimeout(() => {
          void reassertAvailable('pass 1');
          // Second pass well beyond any plausible beacon delivery delay.
          refreshRecoverySecondTimer = setTimeout(() => {
            void reassertAvailable('pass 2');
          }, 4000);
        }, 1500);
      }
    } catch {
      // sessionStorage not available — ignore
    }

    return () => {
      if (initialSyncTimer) clearTimeout(initialSyncTimer);
      if (refreshRecoveryTimer) clearTimeout(refreshRecoveryTimer);
      if (refreshRecoverySecondTimer) clearTimeout(refreshRecoverySecondTimer);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('click', handleLinkClick, { capture: true });
      document.removeEventListener('submit', handleFormSubmit, { capture: true });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      clearInterval(heartbeatInterval);
      clearInterval(idleCheckInterval);
      if (channelRef.current) {
        removeRealtimeChannel(channelRef.current);
      }
    };
  }, [user?.user?.id, getUserAgentInfo]);

  // Update presence when page changes
  useEffect(() => {
    if (!channelRef.current || !user?.user?.id) return;

    const { device, browser, os } = getUserAgentInfo();
    channelRef.current.track({
      userId: user.user.id,
      name: user.user.name || "Unknown",
      email: user.user.email || "",
      userType: user.user.userType || "agent",
      currentPage: getPageName(pathname),
      lastSeen: new Date().toISOString(),
      device,
      browser,
      os,
      status: isIdleRef.current ? ("idle" as const) : ("online" as const),
    });
  }, [pathname, user?.user?.id, getPageName, getUserAgentInfo]);
}

/**
 * Hook to subscribe to all users' presence (for admin panel)
 * Returns real-time presence data for all online users
 */
export function usePresenceSubscription() {
  const [presenceState, setPresenceState] = useState<Record<string, UserPresenceState[]>>({});
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    let channel: RealtimeChannel;
    let isMounted = true;
    const pollRef: { current: ReturnType<typeof setInterval> | null } = { current: null };

    const setupSubscription = async () => {
      try {
        const supabase = await getSupabaseClient();

        // Subscribe to the same channel that broadcasters use
        channel = supabase.channel("user-presence", {
          config: {
            presence: {
              key: "admin-listener", // Unique key for the listener
            },
          },
        });

        // ⚠️ supabase-js returns the SAME RealtimeChannel instance for a given topic, and
        // `usePresenceBroadcast` joins this exact topic ("user-presence") first. From
        // supabase-js 2.101 onward, adding a `presence` listener to an ALREADY-SUBSCRIBED
        // channel throws:
        //   "cannot add presence callbacks for realtime:user-presence after subscribe()"
        // (Before 2.101 it was silently tolerated, which is why this only appeared after
        // the 2.99 -> 2.112 upgrade.)
        //
        // If the broadcaster got here first the channel is already joined and carries the
        // presence state we want — so read it directly instead of re-registering. The
        // in-memory `presenceState()` needs no network, so a light poll is cheap and keeps
        // this hook working without fighting the other one for ownership of the channel.
        if (channel.state === "joined") {
          const readState = () => {
            if (!isMounted) return;
            setPresenceState(
              channel.presenceState() as unknown as Record<string, UserPresenceState[]>,
            );
            setIsConnected(true);
          };
          readState();
          // ⚠️ The interval handle MUST live in the effect's scope, not be returned from
          // this async function — `setupSubscription()` is called without awaiting or
          // using its return value, so a cleanup returned from here is silently dropped
          // and the interval runs forever. That leaked one 3s timer per mount, which the
          // Hierarchy live panel makes obvious because it mounts on every node click.
          pollRef.current = setInterval(readState, 3000);
          // Deliberately NOT recorded in channelRef: this hook did not create the
          // channel, the broadcaster did. Tearing it down here would kill the current
          // user's own presence broadcast app-wide.
          return;
        }

        channel
          .on("presence", { event: "sync" }, () => {
            if (isMounted) {
              const state = channel.presenceState();
              setPresenceState(state as unknown as Record<string, UserPresenceState[]>);
            }
          })
          .on("presence", { event: "join" }, ({ key, newPresences }) => {
            if (isMounted) {
              setPresenceState((prev) => ({
                ...prev,
                [key]: newPresences as unknown as UserPresenceState[],
              }));
            }
          })
          .on("presence", { event: "leave" }, ({ key }) => {
            if (isMounted) {
              setPresenceState((prev) => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
              });
            }
          })
          .subscribe(async (status, err) => {
            if (isMounted) {
              setIsConnected(status === "SUBSCRIBED");
              if (err) {
                setConnectionError(err.message);
              } else {
                setConnectionError(null);
              }
              // Track presence to join the channel (required for receiving presence events)
              if (status === "SUBSCRIBED") {
                await channel.track({ listener: true, joinedAt: new Date().toISOString() });
              }
            }
          });

        channelRef.current = channel;
      } catch (error) {
        console.error("[Presence] Setup error:", error);
        if (isMounted) {
          setConnectionError(error instanceof Error ? error.message : "Failed to connect");
        }
      }
    };

    setupSubscription();

    return () => {
      isMounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
      if (channelRef.current) {
        removeRealtimeChannel(channelRef.current);
      }
    };
  }, []);

  // Transform presence state to flat array, filtering out non-user presences (like admin-listener)
  const onlineUsers = Object.entries(presenceState)
    .filter(([key]) => key !== "admin-listener") // Exclude the admin listener
    .map(([, presences]) => {
      const latestPresence = presences[presences.length - 1];
      return latestPresence;
    })
    .filter((u) => u.userId); // Only include entries with a valid userId

  return {
    onlineUsers,
    presenceState,
    isConnected,
    connectionError,
    onlineCount: onlineUsers.filter((u) => u.status === "online").length,
    idleCount: onlineUsers.filter((u) => u.status === "idle").length,
  };
}
